import React, { memo, useEffect, useState, ChangeEvent, useCallback } from 'react'
import moment from 'moment-timezone'
import { makeStyles } from 'transfix-ui/core'
import { MaterialUiPickersDate } from 'transfix-ui/core/pickers'
import { IStop } from 'apps/fleet-planner/types/loads'
import { TIME_INPUT_FORMAT, TimeInput, parseTime, DatePicker } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

interface IDropTrailerTimestampProps {
  stop: Partial<IStop>
  onStopChange: (stop: Partial<IStop>) => void
  variant?: string
  timeFormat: TIME_INPUT_FORMAT
  /**
   * @default true
   */
  isDropTrailerTimeVisible?: boolean
}

const useDropTrailerTimestampStyles = makeStyles(theme => ({
  dateContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '2fr 1fr 2fr',
  },
}))

export const DropTrailerTimestamp = memo<IDropTrailerTimestampProps>(
  ({
    stop,
    onStopChange,
    timeFormat = TIME_INPUT_FORMAT.MILITARY_SHORT,
    isDropTrailerTimeVisible = true,
  }) => {
    const styles = useDropTrailerTimestampStyles()
    const { displayTimezone, dropTrailerArrivedAt, dropTrailerEmptyAt, stopSequence, stopType } =
      stop || {}

    const isPickup = stopType === 'pickup'

    const inputLabel = isPickup ? 'Arrival Time' : 'Empty Time'

    const [dropTrailerTime, setDropTrailerTime] = useState(
      (isPickup ? dropTrailerArrivedAt : dropTrailerEmptyAt) || moment().format()
    )

    const onDropTrailerTimeChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        const time = moment(value || '', 'h:mma')
        const formattedDate = moment(dropTrailerTime || '')
          .tz(displayTimezone || moment.tz.guess())
          .set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: 0,
          })
          .format()
        setDropTrailerTime(formattedDate)
      },
      [dropTrailerTime]
    )

    const time = moment(dropTrailerTime).tz(displayTimezone || moment.tz.guess())

    const dateChangeHandler = (date: MaterialUiPickersDate) => {
      const formattedDate = moment(date)
        .tz(displayTimezone || moment.tz.guess())
        .set({
          hour: time.get('hour'),
          minute: time.get('minute'),
          second: 0,
        })
        .format()
      setDropTrailerTime(formattedDate)
    }

    useEffect(() => {
      const dropTrailerTimeFormatted = moment(dropTrailerTime)
        .tz(displayTimezone || moment.tz.guess())
        .utc()
        .format()

      onStopChange({
        dropTrailerEmptyAt: !isPickup ? dropTrailerTimeFormatted : null,
        dropTrailerArrivedAt: isPickup ? dropTrailerTimeFormatted : null,
      })
    }, [dropTrailerTime])

    return (
      <>
        {isDropTrailerTimeVisible && (
          <>
            <label className='label' htmlFor='check-in'>
              {inputLabel}
            </label>
            <div className='row' data-test={`inline-edit-inout-${stopType}-${stopSequence}`}>
              <div className={styles.dateContainer}>
                <DatePicker
                  id='date'
                  onChange={dateChangeHandler}
                  value={moment(dropTrailerTime || '').format(FORMATS.dateFormat)}
                  fullWidth={false}
                />
                <TimeInput
                  timeFormat={timeFormat}
                  placeholder={DISPLAY.emptyDateTime}
                  value={
                    parseTime(
                      time
                        .tz(displayTimezone || moment.tz.guess())
                        .format(TIME_INPUT_FORMAT.MILITARY_SHORT),
                      TIME_INPUT_FORMAT.MILITARY_SHORT
                    ) || ''
                  }
                  onChange={onDropTrailerTimeChangeHandler}
                  dataTest={`stop-check-in-time-${stopType}-${stopSequence}`}
                  large={false}
                />
              </div>
            </div>
          </>
        )}
      </>
    )
  }
)
